import { useState } from 'react';
import UploadZone from '../components/UploadZone';
import ModelViewer from '../components/ModelViewer';
import { useModelStore } from '../store/modelStore';
import { useAuthStore } from '../store/authStore';
import LoginRequired from '../components/LoginRequired';

export default function UploadPage() {
  const { isAuthenticated } = useAuthStore();
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const addModel = useModelStore((s) => s.addModel);

  const handleFileSelect = (selected: File) => {
    setFile(selected);
    setPreviewUrl(URL.createObjectURL(selected));
    if (!name) setName(selected.name.replace(/\.(glb|gltf)$/i, ''));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !name) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('description', description);
    formData.append('isPublic', String(isPublic));

    try {
      const { apiFetch } = await import('../lib/api');
      const res = await apiFetch('/api/models/upload', {
        method: 'POST',
        body: formData,
      });
      const data = (await res.json()) as { error?: string; model?: import('../types').Model };
      if (!res.ok) {
        throw new Error(
          res.status === 401 ? 'Please log in first' : (data.error || 'Upload failed'),
        );
      }
      if (data.model) addModel(data.model);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setFile(null);
      setPreviewUrl(null);
      setName('');
      setDescription('');
      setIsPublic(false);
      setIsUploading(false);
      alert('Upload complete');
      return;
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="container" style={{ paddingTop: '1.5rem' }}>
        <h2 style={{ marginBottom: '1rem' }}>Upload Model</h2>
        <LoginRequired />
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '1.5rem' }}>
      <h2 style={{ marginBottom: '1rem' }}>Upload Model</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {!previewUrl ? (
            <UploadZone onFileSelect={handleFileSelect} />
          ) : (
            <div className="card">
              <p style={{ margin: '0 0 0.5rem' }}>
                <strong>{file?.name}</strong>
              </p>
              <button onClick={() => { setFile(null); setPreviewUrl(null); }}>Remove</button>
            </div>
          )}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <input
              placeholder="Model name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <textarea
              placeholder="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
              Make this model public
            </label>
            <button type="submit" disabled={!file || !name || isUploading}>
              {isUploading ? 'Uploading...' : 'Upload'}
            </button>
          </form>
        </div>
        <div className="card" style={{ minHeight: '400px', padding: 0, overflow: 'hidden' }}>
          {previewUrl ? <ModelViewer url={previewUrl} /> : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--muted)' }}>
              Preview will appear here
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
